import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    mensagem: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const serenaParam = urlParams.get('serena');
    if (serenaParam !== null) {
      setTimeout(() => {
        openSerena();
      }, 1500);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d40d06rg4k0q4oql94a0',
        {
          method: 'POST',
          body: formDataToSend,
        },
      );

      if (response.ok) {
        setSubmitStatus('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setFormData({ nome: '', email: '', telefone: '', mensagem: '' });
      } else {
        setSubmitStatus('Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const openSerena = () => {
    const serenaButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (serenaButton) {
      serenaButton.click();
      setTimeout(() => {
        const chatInput = document.querySelector('[data-vapi-chat-input]') as HTMLInputElement;
        if (chatInput) {
          const event = new CustomEvent('vapi-widget-opened', { detail: { autoGreet: true } });
          window.dispatchEvent(event);
        }
      }, 500);
    }
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/5521994740504?text=Olá, quero equilibrar minha mente agora', '_blank');
  };

  // Logo palette — cores exatas da logo
  const cyan = '#00C8E0';
  const green = '#00C896';
  const gold = '#F0A830';
  const darkBg = '#050F1A';       // fundo escuro da logo
  const darkCard = '#0A1E2E';     // cards escuros
  const darkSection = '#071525';  // seções escuras alternadas
  const textLight = '#B8E8F0';    // texto claro sobre fundo escuro
  const textMuted = '#6AACBA';    // texto secundário

  return (
    <div className="min-h-screen" style={{ backgroundColor: darkBg }}>

      {/* Header */}
      <header
        className="fixed w-full top-0 z-50 transition-all duration-300"
        style={{
          backgroundColor: isScrolled ? `${darkBg}F2` : 'transparent',
          backdropFilter: isScrolled ? 'blur(12px)' : 'none',
          borderBottom: isScrolled ? `1px solid #0D2B3E` : 'none',
        }}
      >
        <nav className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
              >
                <i className="ri-brain-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Pacifico, serif' }}>
                  Equilibrada🧠Mente™
                </h1>
                <p className="text-xs" style={{ color: gold }}>Reginaldo Silva</p>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {['inicio', 'sobre', 'servicos', 'depoimentos'].map((id, idx) => (
                <button
                  key={id}
                  onClick={() => scrollToSection(id)}
                  className="transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
                  style={{ color: textLight }}
                  onMouseEnter={e => (e.currentTarget.style.color = cyan)}
                  onMouseLeave={e => (e.currentTarget.style.color = textLight)}
                >
                  {['Início', 'Sobre', 'Especialidades', 'Depoimentos'][idx]}
                </button>
              ))}
              <button
                onClick={openSerena}
                className="text-white px-6 py-2 rounded-full transition-opacity cursor-pointer whitespace-nowrap text-sm font-semibold"
                style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Agendar Consulta
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-white cursor-pointer">
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4" style={{ borderTop: `1px solid #0D2B3E` }}>
              <div className="flex flex-col space-y-3 pt-4">
                {['inicio', 'sobre', 'servicos', 'depoimentos'].map((id, idx) => (
                  <button
                    key={id}
                    onClick={() => scrollToSection(id)}
                    className="transition-colors text-left cursor-pointer text-sm"
                    style={{ color: textLight }}
                  >
                    {['Início', 'Sobre', 'Especialidades', 'Depoimentos'][idx]}
                  </button>
                ))}
                <button
                  onClick={openSerena}
                  className="text-white px-6 py-2 rounded-full transition-opacity text-left cursor-pointer whitespace-nowrap font-semibold text-sm"
                  style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                >
                  Agendar Consulta
                </button>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="flex flex-col">
        {/* Foto de cima — sem overlay, imagem limpa */}
        <div className="w-full pt-20" style={{ backgroundColor: darkBg }}>
          <img
            src="https://static.readdy.ai/image/358c420565fea72b91269b0160885e8f/5d2a763a9d58ce9aa17d4d1298a318bd.jpeg"
            alt="Equilibrada Mente - Reginaldo Silva"
            className="w-full object-cover object-top"
            style={{ maxHeight: '600px' }}
          />
        </div>

        {/* Bloco de texto abaixo da foto */}
        <div className="w-full py-12" style={{ backgroundColor: darkBg }}>
          <div className="max-w-6xl mx-auto px-6">
            <div
              className="p-8 rounded-2xl"
              style={{
                backgroundColor: darkCard,
                border: `1px solid rgba(0,200,224,0.25)`,
                borderTop: `3px solid ${cyan}`,
              }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                Equilibrada🧠Mente™
              </h2>
              <p className="text-lg mb-4 font-medium" style={{ color: cyan }}>
                Um novo caminho para a tranquilidade em meio ao caos da ansiedade.
              </p>
              <p className="text-base mb-4 leading-relaxed" style={{ color: textLight }}>
                A mente feminina é poderosa — mas quando sobrecarregada, pode se perder entre medos, cobranças e inseguranças. O método Equilibrada🧠Mente™, criado pelo psicoterapeuta Reginaldo Silva, integra hipnose clínica, psicanálise, PNL e neurociência aplicada para restaurar o equilíbrio mental e emocional com resultados perceptíveis desde a primeira sessão.
              </p>
              <p className="text-base italic mb-6" style={{ color: gold }}>
                "Equilibrar sua mente é reencontrar sua essência."
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-lg" style={{ color: gold }}></i>
                  <span className="text-sm" style={{ color: textLight }}>Atendimento online e presencial (RJ)</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-leaf-line text-lg" style={{ color: green }}></i>
                  <span className="text-sm" style={{ color: textLight }}>Programa terapêutico exclusivo de 12 sessões</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-chat-3-line text-lg" style={{ color: cyan }}></i>
                  <span className="text-sm" style={{ color: textLight }}>Avaliação inicial personalizada</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={openSerena}
                  className="text-white px-6 py-3 rounded-full text-base font-semibold transition-opacity cursor-pointer whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  Quero reequilibrar minha mente
                </button>
                <button
                  onClick={openWhatsApp}
                  className="text-white px-6 py-3 rounded-full text-base font-semibold transition-opacity flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                  style={{ backgroundColor: '#128C7E' }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  <i className="ri-whatsapp-line"></i>
                  (21) 99474-0504
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-20" style={{ backgroundColor: darkSection }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-white mb-4">💼 SOBRE REGINALDO SILVA</h3>
            <p className="text-xl" style={{ color: textMuted }}>Criador do método Equilibrada🧠Mente™</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h4 className="text-3xl font-bold text-white mb-6">Sobre o Psicoterapeuta</h4>
              <div className="space-y-4 leading-relaxed" style={{ color: textLight }}>
                <p><strong style={{ color: cyan }}>Reginaldo Silva</strong> — Criador do método Equilibrada🧠Mente™</p>
                <p>
                  Psicoterapeuta clínico com 9 anos de experiência, especialista em Hipnose Clínica, PNL, Psicanálise e Neurociência aplicada à psicoterapia. Com uma abordagem científica e humanizada, Reginaldo tem ajudado mulheres a superarem ansiedade, depressão, fobia social e síndrome do pânico, conduzindo-as a uma vida emocional mais leve e equilibrada.
                </p>
                <p className="text-lg italic font-medium" style={{ color: gold }}>
                  "A transformação começa quando você entende que cuidar da mente é cuidar da vida."
                </p>
              </div>
              <div className="mt-8 space-y-3">
                {[
                  { icon: 'ri-medal-line', text: '9 anos de experiência clínica' },
                  { icon: 'ri-user-heart-line', text: '300+ pacientes acompanhadas' },
                  { icon: 'ri-map-pin-line', text: 'Atendimento online e presencial (RJ)' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center space-x-3">
                    <i className={`${item.icon} text-xl`} style={{ color: gold }}></i>
                    <span style={{ color: textLight }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://static.readdy.ai/image/358c420565fea72b91269b0160885e8f/dc60281b2615f2c02e05647f181ce6bd.jpeg"
                alt="Reginaldo Silva - Criador do método Equilibrada🧠Mente™"
                className="rounded-xl w-full object-cover object-top"
                style={{ border: `2px solid ${cyan}` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-20" style={{ backgroundColor: darkBg }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">🧠 ESPECIALIDADES E SERVIÇOS</h3>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: textMuted }}>Especialidades do Método Equilibrada🧠Mente™</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {[
              { icon: 'ri-heart-pulse-line', color: cyan, title: 'Ansiedade', desc: 'Técnicas avançadas para controlar pensamentos acelerados, preocupações excessivas e sintomas físicos da ansiedade.' },
              { icon: 'ri-sun-line', color: gold, title: 'Depressão', desc: 'Abordagem integrativa para superar tristeza profunda, falta de energia e recuperar o prazer de viver.' },
              { icon: 'ri-shield-cross-line', color: green, title: 'Síndrome do Pânico', desc: 'Eliminação de crises de pânico através de técnicas de dessensibilização e controle emocional.' },
              { icon: 'ri-group-line', color: gold, title: 'Fobia Social', desc: 'Desenvolvimento de confiança para interações sociais e superação do medo de julgamento.' },
              { icon: 'ri-heart-3-line', color: cyan, title: 'Autoestima', desc: 'Fortalecimento da autoimagem e desenvolvimento de amor próprio saudável e duradouro.' },
              { icon: 'ri-hearts-line', color: green, title: 'Relacionamentos', desc: 'Melhoria da comunicação, resolução de conflitos e construção de vínculos saudáveis.' },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl p-6 transition-all duration-300 cursor-default"
                style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = item.color)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#0D2B3E')}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${item.color}18` }}
                >
                  <i className={`${item.icon} text-2xl`} style={{ color: item.color }}></i>
                </div>
                <h4 className="text-xl font-bold text-white mb-3 text-center">{item.title}</h4>
                <p className="text-center leading-relaxed text-sm" style={{ color: textMuted }}>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Therapy Program */}
          <div className="rounded-xl p-8 mb-16" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h4 className="text-3xl font-bold text-white mb-6">Programa Terapêutico de 12 Sessões</h4>
                <div className="space-y-4" style={{ color: textLight }}>
                  <p>
                    O método Equilibrada🧠Mente™ foi desenvolvido especificamente para mulheres que buscam tranquilidade, clareza e equilíbrio emocional duradouro através de uma abordagem científica e humanizada.
                  </p>
                  <div className="space-y-3">
                    {[
                      'Avaliação completa e diagnóstico personalizado',
                      'Técnicas de hipnose clínica e PNL',
                      'Ferramentas de autogerenciamento emocional',
                      'Acompanhamento contínuo e suporte',
                      'Resultados mensuráveis e duradouros',
                    ].map((item) => (
                      <div key={item} className="flex items-center space-x-3">
                        <i className="ri-check-line text-xl" style={{ color: gold }}></i>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="https://static.readdy.ai/image/358c420565fea72b91269b0160885e8f/6adef3b4c7affecc95eff1087f99ac2e.jpeg"
                  alt="Ambiente terapêutico - Programa de 12 sessões Equilibrada🧠Mente™"
                  className="rounded-xl w-full object-cover object-top"
                  style={{ border: `1px solid #0D2B3E` }}
                />
              </div>
            </div>
          </div>

          {/* Investment Section */}
          <div className="rounded-xl p-8" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
            <h4 className="text-3xl font-bold text-white mb-6 text-center">💎 INVISTA NO SEU BEM-ESTAR</h4>
            <p className="text-center mb-8 max-w-2xl mx-auto" style={{ color: textMuted }}>
              O primeiro passo para transformar sua vida é cuidar de você. O método Equilibrada🧠Mente™ foi desenvolvido para mulheres que buscam tranquilidade, clareza e equilíbrio emocional duradouro.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div
                className="text-center p-6 rounded-xl border-2"
                style={{ borderColor: cyan, backgroundColor: `${cyan}0D` }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${cyan}20` }}
                >
                  <i className="ri-calendar-check-line text-2xl" style={{ color: cyan }}></i>
                </div>
                <h5 className="text-xl font-semibold text-white mb-3">💰 Avaliação Inicial</h5>
                <p className="mb-4 text-sm" style={{ color: textMuted }}>Sessão individual de 50 a 60 min</p>
                <ul className="text-sm space-y-2" style={{ color: textLight }}>
                  <li>• Identificação das causas emocionais</li>
                  <li>• Estratégia terapêutica personalizada</li>
                  <li>• Primeira vivência terapêutica</li>
                </ul>
              </div>

              <div
                className="text-center p-6 rounded-xl border-2"
                style={{ borderColor: gold, backgroundColor: `${gold}0D` }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${gold}20` }}
                >
                  <i className="ri-leaf-line text-2xl" style={{ color: gold }}></i>
                </div>
                <h5 className="text-xl font-semibold text-white mb-3">🌿 Programa de 12 Sessões</h5>
                <p className="mb-4 text-sm" style={{ color: textMuted }}>Informações na avaliação</p>
                <ul className="text-sm space-y-2" style={{ color: textLight }}>
                  <li>• Atendimento presencial ou online</li>
                  <li>• Programa estruturado com acompanhamento</li>
                  <li>• Ferramentas de autogerenciamento emocional</li>
                  <li>• Resultados progressivos e duradouros</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-lg italic mb-6" style={{ color: textLight }}>
                "Você não precisa enfrentar tudo sozinha.<br />
                O equilíbrio começa quando você escolhe cuidar de si."
              </p>
              <button
                onClick={openSerena}
                className="text-white px-8 py-4 rounded-full text-lg font-semibold transition-opacity cursor-pointer whitespace-nowrap"
                style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Quero reequilibrar minha mente
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="depoimentos" className="py-20" style={{ backgroundColor: darkSection }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">💬 DEPOIMENTOS</h3>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: textMuted }}>
              Veja o que nossas pacientes falam sobre sua transformação com o método Equilibrada🧠Mente™
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {/* Testimonial 1 - Vídeo */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="aspect-[9/16] rounded-lg overflow-hidden mb-4">
                <iframe
                  src="https://www.youtube.com/embed/U60zRK9ZZh0"
                  title="Depoimento Eliza Alves"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mb-2">
                <h5 className="font-semibold text-white">Eliza Alves</h5>
                <p className="text-sm" style={{ color: textMuted }}>Moradora do Japão há 20 anos</p>
              </div>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
            </div>

            {/* Testimonial 2 - Vídeo */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="aspect-[9/16] rounded-lg overflow-hidden mb-4">
                <iframe
                  src="https://www.youtube.com/embed/4ZfFFe1su9I"
                  title="Depoimento Lucas Nóbrega"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mb-2">
                <h5 className="font-semibold text-white">Lucas Nóbrega</h5>
                <p className="text-sm" style={{ color: textMuted }}>Empresário do ramo alimentício</p>
              </div>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
            </div>

            {/* Testimonial 3 - Vídeo */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="aspect-[9/16] rounded-lg overflow-hidden mb-4">
                <iframe
                  src="https://www.youtube.com/embed/A4feGcAG1eE"
                  title="Depoimento Márcia Ferrari"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mb-2">
                <h5 className="font-semibold text-white">Márcia Ferrari</h5>
                <p className="text-sm" style={{ color: textMuted }}>Dona de casa</p>
              </div>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="flex items-center mb-4">
                <img
                  src="https://public.readdy.ai/ai/img_res/b56aa54732229e5cee7c347d325bb450.jpg"
                  alt="Fernanda Lima - Designer"
                  className="w-12 h-12 rounded-full object-cover object-top"
                  style={{ border: `2px solid ${cyan}` }}
                />
                <div className="ml-4">
                  <h5 className="font-semibold text-white">Fernanda Lima</h5>
                  <p className="text-sm" style={{ color: textMuted }}>Designer, 29 anos</p>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
              <p className="leading-relaxed text-sm" style={{ color: textLight }}>
                "A fobia social me impedia de apresentar meus projetos e crescer profissionalmente. Com as técnicas do Reginaldo, desenvolvi confiança e hoje apresento com naturalidade. Minha carreira decolou!"
              </p>
            </div>

            {/* Testimonial 5 */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="flex items-center mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Experienced%20female%20psychologist%20in%20her%2040s%20with%20wise%20and%20compassionate%20expression%2C%20wearing%20professional%20attire%2C%20therapy%20office%20background%20with%20diplomas%20and%20books%2C%20warm%20lighting%2C%20trustworthy%20appearance&width=60&height=60&seq=testimonial5&orientation=squarish"
                  alt="Carla Mendes - Psicóloga"
                  className="w-12 h-12 rounded-full object-cover object-top"
                  style={{ border: `2px solid ${green}` }}
                />
                <div className="ml-4">
                  <h5 className="font-semibold text-white">Carla Mendes</h5>
                  <p className="text-sm" style={{ color: textMuted }}>Psicóloga, 42 anos</p>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
              <p className="leading-relaxed text-sm" style={{ color: textLight }}>
                "Mesmo sendo psicóloga, precisava de ajuda para superar traumas pessoais. O método do Reginaldo me proporcionou uma transformação profunda. Hoje sou uma profissional e pessoa mais completa."
              </p>
            </div>

            {/* Testimonial 6 */}
            <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <div className="flex items-center mb-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Dedicated%20female%20nurse%20in%20her%2030s%20with%20caring%20expression%2C%20wearing%20medical%20scrubs%2C%20hospital%20background%20with%20medical%20equipment%2C%20professional%20lighting%2C%20compassionate%20and%20strong%20appearance&width=60&height=60&seq=testimonial6&orientation=squarish"
                  alt="Patrícia Rocha - Enfermeira"
                  className="w-12 h-12 rounded-full object-cover object-top"
                  style={{ border: `2px solid ${gold}` }}
                />
                <div className="ml-4">
                  <h5 className="font-semibold text-white">Patrícia Rocha</h5>
                  <p className="text-sm" style={{ color: textMuted }}>Enfermeira, 36 anos</p>
                </div>
              </div>
              <div className="flex mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill" style={{ color: gold }}></i>
                ))}
              </div>
              <p className="leading-relaxed text-sm" style={{ color: textLight }}>
                "O estresse do trabalho hospitalar estava me consumindo. Através do método Equilibrada🧠Mente™, aprendi a gerenciar minhas emoções e hoje trabalho com mais leveza e eficiência."
              </p>
            </div>
          </div>

          {/* Results Section */}
          <div className="rounded-xl p-8" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
            <h4 className="text-3xl font-bold text-white mb-8 text-center">📊 RESULTADOS COMPROVADOS</h4>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold mb-2" style={{ color: gold }}>95%</div>
                <p style={{ color: textLight }}>Das pacientes relatam melhora significativa já na primeira sessão</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2" style={{ color: cyan }}>300+</div>
                <p style={{ color: textLight }}>Mulheres transformadas pelo método Equilibrada🧠Mente™</p>
              </div>
              <div>
                <div className="text-5xl font-bold mb-2" style={{ color: green }}>9 anos</div>
                <p style={{ color: textLight }}>De experiência clínica especializada em saúde mental feminina</p>
              </div>
            </div>
            <div className="text-center mt-8">
              <button
                onClick={openSerena}
                className="text-white px-8 py-4 rounded-full text-lg font-semibold transition-opacity cursor-pointer whitespace-nowrap"
                style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
              >
                Quero reequilibrar minha mente
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-20" style={{ backgroundColor: darkBg }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">📞 ENTRE EM CONTATO</h3>
            <p className="text-xl max-w-3xl mx-auto" style={{ color: textMuted }}>
              Dê o primeiro passo para equilibrar sua mente. Entre em contato e agende sua avaliação inicial.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="rounded-xl p-8" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
              <h4 className="text-2xl font-bold text-white mb-6">Envie sua mensagem</h4>
              <form onSubmit={handleSubmit} id="contact-form" className="space-y-6" data-readdy-form>
                {[
                  { id: 'nome', label: 'Nome completo *', type: 'text', placeholder: 'Seu nome completo' },
                  { id: 'email', label: 'E-mail *', type: 'email', placeholder: 'seu@email.com' },
                  { id: 'telefone', label: 'Telefone/WhatsApp *', type: 'tel', placeholder: '(21) 99999-9999' },
                ].map(({ id, label, type, placeholder }) => (
                  <div key={id}>
                    <label htmlFor={id} className="block text-sm font-medium mb-2" style={{ color: textLight }}>{label}</label>
                    <input
                      type={type}
                      id={id}
                      name={id}
                      value={formData[id as keyof typeof formData]}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 rounded-lg text-sm text-white"
                      style={{
                        backgroundColor: darkSection,
                        border: `1px solid #0D2B3E`,
                        outline: 'none',
                      }}
                      onFocus={e => (e.currentTarget.style.borderColor = cyan)}
                      onBlur={e => (e.currentTarget.style.borderColor = '#0D2B3E')}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
                <div>
                  <label htmlFor="mensagem" className="block text-sm font-medium mb-2" style={{ color: textLight }}>Mensagem *</label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    value={formData.mensagem}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg text-sm resize-none text-white"
                    style={{
                      backgroundColor: darkSection,
                      border: `1px solid #0D2B3E`,
                      outline: 'none',
                    }}
                    onFocus={e => (e.currentTarget.style.borderColor = cyan)}
                    onBlur={e => (e.currentTarget.style.borderColor = '#0D2B3E')}
                    placeholder="Conte um pouco sobre o que você está passando e como podemos ajudar..."
                  ></textarea>
                  <p className="text-xs mt-1" style={{ color: textMuted }}>Máximo 500 caracteres ({formData.mensagem.length}/500)</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white py-3 px-6 rounded-lg font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                  onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.opacity = '0.85'; }}
                  onMouseLeave={e => { if (!isSubmitting) e.currentTarget.style.opacity = '1'; }}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar mensagem'}
                </button>
                {submitStatus && (
                  <div className={`text-center p-3 rounded-lg ${submitStatus.includes('sucesso') ? 'bg-green-900/40 text-green-300' : 'bg-red-900/40 text-red-300'}`}>
                    {submitStatus}
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h4 className="text-2xl font-bold text-white mb-6">Informações de contato</h4>
                <div className="space-y-6">
                  {[
                    { icon: 'ri-whatsapp-line', color: green, title: 'WhatsApp', content: '(21) 99474-0504', action: openWhatsApp, actionLabel: 'Enviar mensagem' },
                    { icon: 'ri-mail-line', color: cyan, title: 'E-mail', content: 'contato@equilibradamente.com.br' },
                    { icon: 'ri-map-pin-line', color: gold, title: 'Localização', content: 'Rio de Janeiro, RJ', sub: 'Atendimento presencial e online' },
                    { icon: 'ri-time-line', color: cyan, title: 'Horário de atendimento', content: 'Segunda a sexta: 8h às 18h', sub: 'Sábado: 8h às 12h' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start space-x-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${item.color}18` }}
                      >
                        <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                      </div>
                      <div>
                        <h5 className="font-semibold text-white mb-1">{item.title}</h5>
                        <p style={{ color: textLight }}>{item.content}</p>
                        {item.sub && <p className="text-sm" style={{ color: textMuted }}>{item.sub}</p>}
                        {item.action && (
                          <button onClick={item.action} className="text-sm cursor-pointer" style={{ color: cyan }}>
                            {item.actionLabel}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl p-6" style={{ backgroundColor: darkCard, border: `1px solid #0D2B3E` }}>
                <h5 className="font-semibold text-white mb-3">💡 Primeira consulta</h5>
                <p className="mb-4 text-sm" style={{ color: textLight }}>
                  A avaliação inicial é o primeiro passo para sua transformação. Nesta sessão, identificamos as causas dos seus desafios emocionais e traçamos uma estratégia personalizada.
                </p>
                <button
                  onClick={openSerena}
                  className="text-white px-6 py-3 rounded-lg font-semibold transition-opacity cursor-pointer whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  Agendar avaliação
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ backgroundColor: '#020C14', borderTop: `1px solid #0D2B3E` }} className="text-white py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${cyan}, ${green})` }}
                >
                  <i className="ri-brain-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold" style={{ fontFamily: 'Pacifico, serif' }}>
                    Equilibrada🧠Mente™
                  </h4>
                  <p className="text-sm" style={{ color: gold }}>Reginaldo Silva</p>
                </div>
              </div>
              <p className="mb-4 leading-relaxed text-sm" style={{ color: textMuted }}>
                Método exclusivo que integra hipnose clínica, psicanálise, PNL e neurociência para restaurar o equilíbrio mental e emocional feminino com resultados perceptíveis desde a primeira sessão.
              </p>
              <div className="flex space-x-4">
                {[openWhatsApp, openSerena].map((fn, i) => (
                  <button
                    key={i}
                    onClick={fn}
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer"
                    style={{ backgroundColor: '#0D2B3E' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = cyan)}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#0D2B3E')}
                  >
                    <i className={i === 0 ? 'ri-whatsapp-line' : 'ri-mail-line'}></i>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h5 className="font-semibold mb-4" style={{ color: cyan }}>Links rápidos</h5>
              <ul className="space-y-2">
                {[
                  { id: 'inicio', label: 'Início' },
                  { id: 'sobre', label: 'Sobre' },
                  { id: 'servicos', label: 'Especialidades' },
                  { id: 'depoimentos', label: 'Depoimentos' },
                ].map(({ id, label }) => (
                  <li key={id}>
                    <button
                      onClick={() => scrollToSection(id)}
                      className="transition-colors cursor-pointer text-sm"
                      style={{ color: textMuted }}
                      onMouseEnter={e => (e.currentTarget.style.color = gold)}
                      onMouseLeave={e => (e.currentTarget.style.color = textMuted)}
                    >
                      {label}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={openSerena}
                    className="transition-colors cursor-pointer text-sm"
                    style={{ color: textMuted }}
                    onMouseEnter={e => (e.currentTarget.style.color = gold)}
                    onMouseLeave={e => (e.currentTarget.style.color = textMuted)}
                  >
                    Agendar Consulta
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h5 className="font-semibold mb-4" style={{ color: cyan }}>Contato</h5>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <i className="ri-whatsapp-line" style={{ color: green }}></i>
                  <span className="text-sm" style={{ color: textMuted }}>(21) 99474-0504</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-mail-line" style={{ color: cyan }}></i>
                  <span className="text-xs" style={{ color: textMuted }}>contato@equilibradamente.com.br</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-map-pin-line" style={{ color: gold }}></i>
                  <span className="text-sm" style={{ color: textMuted }}>Rio de Janeiro, RJ</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 text-center" style={{ borderTop: `1px solid #0D2B3E` }}>
            <p className="text-xs" style={{ color: '#2A5A6A' }}>
              © 2024 Equilibrada🧠Mente™ - Reginaldo Silva. Todos os direitos reservados. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors cursor-pointer"
                style={{ color: gold }}
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
